import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Printer, Download, Receipt, FileText, CheckCircle2 } from 'lucide-react';
import { formatDate, formatDateTime } from '@/utils/dateTime';

export const InvoicePrintModal = ({
  open,
  onOpenChange,
  invoice,
  patient,
  doctor,
  clinicName,
  hospitalName,
}) => {
  const printRef = React.useRef();

  React.useEffect(() => {
    if (open) {
      document.body.classList.add('modal-printing');
    } else {
      document.body.classList.remove('modal-printing');
    }
    return () => {
      document.body.classList.remove('modal-printing');
    };
  }, [open]);

  if (!invoice) return null;

  const handlePrint = () => {
    window.print();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PAID':
        return <Badge className="bg-emerald-600 text-white font-bold">PAID</Badge>;
      case 'PARTIALLY_PAID':
        return <Badge className="bg-amber-500 text-white font-bold">PARTIALLY PAID</Badge>;
      default:
        return <Badge variant="outline" className="border-red-400 text-red-600 font-bold bg-red-50">UNPAID / PENDING</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 border-0 bg-slate-100">
        <DialogHeader className="p-4 bg-slate-900 text-white rounded-t-xl flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
              <FileText className="h-4 w-4 text-teal-400" />
              Tax Invoice — {invoice.invoiceNumber}
            </DialogTitle>
            <p className="text-[11px] text-slate-400">Official billing and fee schedule document</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={handlePrint}
              className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold shadow"
            >
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / Save PDF
            </Button>
          </div>
        </DialogHeader>

        {/* Printable Sheet */}
        <div
          ref={printRef}
          id="printable-invoice"
          className="p-8 bg-white mx-auto my-4 max-w-2xl w-full rounded-xl shadow-md border border-slate-200 text-slate-800 space-y-6 print:shadow-none print:border-none print:m-0 print:p-6 print:max-w-full"
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-teal-700 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-teal-700 text-white flex items-center justify-center font-bold text-sm">
                  PF
                </div>
                <h2 className="text-xl font-heading font-extrabold text-slate-950 uppercase tracking-tight">
                  {hospitalName || 'PulseFlow Memorial Hospital'}
                </h2>
              </div>
              <p className="text-xs text-slate-600">
                100 Feet Ring Road, Indiranagar, Bengaluru, Karnataka • GSTIN: 29AABCP1234F1Z8
              </p>
              <p className="text-[11px] text-slate-500">
                Department: <span className="font-semibold text-slate-800">{clinicName || 'General OPD Clinic'}</span> • Ph: +91 80 4911 2200
              </p>
            </div>
            <div className="text-right space-y-1">
              <span className="text-xs uppercase font-extrabold tracking-wider text-teal-800 block">
                TAX INVOICE
              </span>
              <p className="text-xs font-mono font-bold text-slate-900">{invoice.invoiceNumber}</p>
              <div className="pt-1">{getStatusBadge(invoice.status)}</div>
            </div>
          </div>

          {/* Invoice & Patient Meta Grid */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs">
            <div className="space-y-1">
              <p className="text-slate-500 uppercase font-semibold text-[10px]">Billed To (Patient)</p>
              <p className="font-bold text-slate-900 text-sm">
                {patient?.firstName ? `${patient.firstName} ${patient.lastName}` : invoice.patientName || 'Patient'}
              </p>
              <p className="text-slate-600">
                ID: <span className="font-mono">{patient?.patientCode || invoice.patientCode || invoice.patientId}</span> • Phone: {patient?.phone || 'N/A'}
              </p>
              <p className="text-slate-600">
                Gender / Age: {patient?.gender || 'N/A'} / {patient?.age ? `${patient.age} yrs` : 'Adult'}
              </p>
            </div>
            <div className="space-y-1 text-right">
              <p className="text-slate-500 uppercase font-semibold text-[10px]">Invoice Details</p>
              <p className="text-slate-700">
                Date: <span className="font-semibold text-slate-900">{formatDate(invoice.issuedAt || invoice.createdAt)}</span>
              </p>
              <p className="text-slate-700">
                Doctor: <span className="font-semibold text-slate-900">{doctor?.name || invoice.doctorName || 'Dr. Consultant'}</span>
              </p>
              <p className="text-slate-700">
                Payment Status: <strong className="text-slate-900">{invoice.status}</strong>
              </p>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="space-y-2">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-200 bg-slate-100 text-slate-700 uppercase text-[10px]">
                  <th className="py-2 px-3 font-bold">#</th>
                  <th className="py-2 px-3 font-bold">Description</th>
                  <th className="py-2 px-3 font-bold text-center">Type</th>
                  <th className="py-2 px-3 font-bold text-center">Qty</th>
                  <th className="py-2 px-3 font-bold text-right">Unit Price (₹)</th>
                  <th className="py-2 px-3 font-bold text-right">Total (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(invoice.items || []).map((it, idx) => (
                  <tr key={it.id || idx}>
                    <td className="py-2.5 px-3 text-slate-500">{idx + 1}</td>
                    <td className="py-2.5 px-3 font-medium text-slate-900">{it.description}</td>
                    <td className="py-2.5 px-3 text-center text-[10px]">
                      <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">
                        {it.itemType || 'SERVICE'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center">{it.quantity}</td>
                    <td className="py-2.5 px-3 text-right font-mono">{Number(it.unitPrice).toFixed(2)}</td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900">
                      {Number(it.amount).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Summary */}
          <div className="border-t-2 border-slate-200 pt-3 flex justify-end">
            <div className="w-64 space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal:</span>
                <span className="font-mono">₹{Number(invoice.subtotal).toFixed(2)}</span>
              </div>
              {Number(invoice.discount) > 0 && (
                <div className="flex justify-between text-emerald-600 font-medium">
                  <span>Discount:</span>
                  <span className="font-mono">-₹{Number(invoice.discount).toFixed(2)}</span>
                </div>
              )}
              {Number(invoice.taxAmount) > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Tax (GST):</span>
                  <span className="font-mono">₹{Number(invoice.taxAmount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold border-t border-slate-200 pt-1.5 text-slate-950">
                <span>Total Amount:</span>
                <span className="font-mono text-teal-800">₹{Number(invoice.totalAmount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-700 font-semibold pt-1 border-t border-dashed border-slate-200">
                <span>Paid to Date:</span>
                <span className="font-mono text-emerald-700">₹{Number(invoice.paidAmount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs font-bold text-red-600">
                <span>Balance Due:</span>
                <span className="font-mono">₹{Number(invoice.balanceAmount).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment History if any */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div className="pt-2 border-t border-slate-200 space-y-2">
              <p className="text-[10px] font-bold uppercase text-slate-500">Payment Transactions</p>
              <div className="space-y-1">
                {invoice.payments.map((p, idx) => (
                  <div
                    key={p.id || idx}
                    className="flex items-center justify-between text-[11px] bg-slate-50 p-2 rounded border border-slate-200"
                  >
                    <span>
                      <strong>{p.paymentMode}</strong> • {formatDateTime(p.paymentDate)}
                      {p.referenceNumber && (
                        <span className="text-slate-500 font-mono ml-2">(Ref: {p.referenceNumber})</span>
                      )}
                    </span>
                    <span className="font-mono font-bold text-emerald-700">
                      ₹{Number(p.amount).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer & Signature */}
          <div className="pt-6 border-t border-slate-200 flex items-end justify-between text-[11px] text-slate-500">
            <div className="space-y-1">
              <p>• Computer-generated invoice; no physical signature required for standard OPD.</p>
              <p>• Fees once paid are non-refundable except under approved medical review.</p>
            </div>
            <div className="text-center space-y-1">
              <div className="w-32 border-b border-slate-400 pb-8" />
              <span className="text-[10px] font-semibold text-slate-700 block">Authorized Cashier</span>
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="text-xs">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicePrintModal;

